import { useState, useEffect } from "react";
import {
	apiUpload,
	apiGet,
	apiDelete,
	RequestConfig,
} from "../../services/api.service";
import { showSuccess, showError } from "../../services/notification.service";

interface WorkPermitProofInfo {
	proof_id: number;
	extra_id: number;
	file_name: string;
	mime_type: string;
	file_size: number;
	uploaded_at: string;
	status?: 'pending' | 'approved' | 'rejected';
	approved_at?: string | null;
	approved_by?: number | null;
	rejected_at?: string | null;
	rejected_by?: number | null;
	rejection_reason?: string | null;
}

export const useWorkPermitProof = (extraId?: number) => {
	const [proofInfo, setProofInfo] = useState<WorkPermitProofInfo | null>(null);
	const [loading, setLoading] = useState(false);
	const [uploading, setUploading] = useState(false);

	// Fetch proof info
	const fetchProofInfo = async () => {
		if (!extraId) return;

		setLoading(true);
		try {
			const config: RequestConfig = {
				notification: { showError: false },
			};

			const response = await apiGet<WorkPermitProofInfo>(
				`/extras/${extraId}/work-permit-proof/info`,
				undefined,
				config
			);

			// Handle the new API behavior:
			// - Success with data: extra exists and has proof
			// - Success with null data: extra exists but has no proof
			// - 404 error: extra doesn't exist
			if (response.success && response.data) {
				setProofInfo(response.data);
			} else if (response.success && response.data === null) {
				// Extra exists but has no proof
				setProofInfo(null);
			} else {
				setProofInfo(null);
			}
		} catch (error: any) {
			if (error.response?.status === 404) {
				// Extra doesn't exist - this is now an actual error
				console.error("Extra not found:", error);
			} else {
				console.error("Error fetching work permit proof info:", error);
			}
			setProofInfo(null);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchProofInfo();
	}, [extraId]);

	// Upload proof
	const uploadProof = async (file: File): Promise<boolean> => {
		if (!extraId) {
			return false;
		}

		setUploading(true);
		try {
			const formData = new FormData();
			formData.append("workPermitProof", file);

			const config: RequestConfig = {
				notification: {
					showError: true,
					showSuccess: true,
				},
			};

			const response = await apiUpload(
				`/extras/${extraId}/work-permit-proof`,
				formData,
				config
			);

			if (response.success && response.data) {
				setProofInfo(response.data);
				// Refetch to ensure we have the latest data
				await fetchProofInfo();
				return true;
			}

			return false;
		} catch (error) {
			console.error("Error uploading work permit proof:", error);
			return false;
		} finally {
			setUploading(false);
		}
	};

	// Download proof
	const downloadProof = async () => {
		if (!extraId || !proofInfo) {
			return;
		}

		try {
			const response = await fetch(
				`${import.meta.env.VITE_API_URL}/extras/${extraId}/work-permit-proof`,
				{
					method: "GET",
					headers: {
						Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
					},
				}
			);

			if (!response.ok) {
				const errorData = await response.json().catch(() => ({}));
				throw new Error(
					errorData.message ||
						errorData.error ||
						"Fehler beim Herunterladen der Datei"
				);
			}

			const blob = await response.blob();
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = proofInfo?.file_name || "arbeitserlaubnis.pdf";
			document.body.appendChild(a);
			a.click();
			window.URL.revokeObjectURL(url);
			document.body.removeChild(a);

			// Download was successful - we can show a generic success message here since it's a client action
			showSuccess("Datei erfolgreich heruntergeladen");
		} catch (error: any) {
			console.error("Error downloading work permit proof:", error);
			showError(error.message || "Download fehlgeschlagen");
		}
	};

	// Delete proof
	const deleteProof = async (): Promise<boolean> => {
		if (!extraId) {
			return false;
		}

		try {
			const config: RequestConfig = {
				notification: {
					showError: true,
					showSuccess: true,
				},
			};

			const response = await apiDelete(
				`/extras/${extraId}/work-permit-proof`,
				config
			);

			if (response.success) {
				setProofInfo(null);
				return true;
			}

			return false;
		} catch (error) {
			console.error("Error deleting work permit proof:", error);
			return false;
		}
	};

	return {
		proofInfo,
		loading,
		uploading,
		uploadProof,
		downloadProof,
		deleteProof,
		refetch: fetchProofInfo,
	};
};
